import { DafPaymentFlow } from "../../blocks/payment/DafPaymentFlow/DafPaymentFlow";
import { FC, useCallback, useContext, useRef } from "react";

import { Typography, useTheme } from "@mui/material";
import { FormType } from "@simplyk/common";

import { useFrontendAuctionContext } from "../../../../../common/contexts/FrontendAuctionContext";
import { FrontendFormContext } from "../../../../../common/contexts/FrontendFormContext";
import { BuyerInformationSection } from "../../blocks/payment/BuyerInformation/BuyerInformationSection";
import { BuyerQuestionsSection } from "../../blocks/payment/BuyerQuestions/BuyerQuestionsSection";
import { callReopenCommandOnFormPaymentError } from "../../blocks/payment/PaymentSection/PaymentElement/callReopenCommandOnFormPaymentError";
import type { PaymentErrorReport } from "../../blocks/payment/PaymentSection/PaymentElement/helper";
import { PaymentErrorContext } from "../../blocks/payment/PaymentSection/PaymentElement/PaymentErrorContext";
import { usePaymentErrorHandler } from "../../blocks/payment/PaymentSection/PaymentElement/usePaymentErrorHandler";
import { PaymentSection } from "../../blocks/payment/PaymentSection/PaymentSection";
import { SummarySection } from "../../blocks/payment/SummarySection/SummarySection";
import { FormUserAgreement } from "../../blocks/payment/SummarySection/UserAgreement/FormUserAgreement";
import { useFormV2Context } from "../../contexts/FormV2Context";
import { useUtmForm } from "../../hooks/useUtmForm";

import { BidderOtpController } from "./BidderOtp/BidderOtpController";
import { useBuyerInformationRenderPosition } from "./hooks/useBuyerInformationRenderPosition";
import { RaffleEligibility } from "./RaffleEligibility/RaffleEligibility";
import { SubmitCommandButton } from "./SubmitButton/SubmitCommandButton";
import { SubmitFreeTicketingButton } from "./SubmitButton/SubmitFreeTicketingButton";
import { SubmitNewBidsButton } from "./SubmitButton/SubmitNewBidsButton";

import { FulfillmentSection } from "@/canvas/features/FormV2/blocks/payment/Fulfillment/FulfillmentSection";
import { ShippingAddressSection } from "@/canvas/features/FormV2/blocks/payment/Fulfillment/ShippingAddressSection";
import { useTrackFormLogoClicked } from "@/canvas/features/FormV2/hooks/useTrackFormLogoClicked";
import { FormV2Step2Layout } from "@/canvas/features/FormV2/steps/step2/FormV2Step2Layout";
import { ThemedZeffyLogo } from "@/canvas/layouts/ThemedBranding/ThemedZeffyLogo";
import { trpc } from "@/common/helpers/trpc";
import { useThemedFormZeffyLogoUrl } from "@/common/hooks/useThemedFormZeffyLogoUrl";
import { useTranslate } from "@/common/hooks/useTranslate";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useIsGhostPreview } from "@/features/LiveFormEditor/LivePreview/useIsGhostPreview";
import { FormButton } from "@/forms-design-system/FormButton";
import { ChevronLeft } from "@/icons/outlined";

export const FormV2Step2: FC = () => {
  const { t } = useTranslate();
  const { isPreview: isLiveFormEditorPreview } = usePreviewContext();
  const isPaymentSectionGhostPreview = useIsGhostPreview("PaymentSection");
  const theme = useTheme();
  const formV2Context = useFormV2Context();
  const { type: formType, formCategory, formObject, setCurrentStep, isAuction } = formV2Context;
  const hasAddOnsStep = formV2Context.type === FormType.Ticketing && formV2Context.hasAddOnsStep;
  const frontendFormContext = useContext(FrontendFormContext);
  const { isEmbed, displayedFormAmount } = frontendFormContext;
  const utm = useUtmForm();
  const { hasAlreadyBid } = useFrontendAuctionContext();

  const onTrackLogoClick = useTrackFormLogoClicked({
    formType,
    formCategory,
    country: formObject.organization?.country,
    formStep: 2,
  });

  const {
    formData: { id: formId },
    commandId,
    organization: formOrganization,
    setIsSubmitting: formSetIsSubmitting,
    setIsPadLoading: formSetIsPadLoading,
    formAmplitudeContext,
    formType: feFormType,
    tip: formTip,
    selectedPaymentMethod: feSelectedPaymentMethod,
    isEmbed: feIsEmbed,
    stripeRecurrenceInterval: feStripeRecurrenceInterval,
    currency: feCurrency,
    category: feCategory,
    prioritizeBankPayment: fePrioritizeBankPayment,
    expressCheckoutDisabled: feExpressCheckoutDisabled,
    paymentMode: fePaymentMode,
  } = frontendFormContext;

  const { mutateAsync: reOpenCommandAsync } = trpc.reOpenCommand.useMutation();
  const onReopenCommandAfterFormPaymentError = useCallback(
    (report: PaymentErrorReport) => {
      void callReopenCommandOnFormPaymentError(reOpenCommandAsync, report);
    },
    [reOpenCommandAsync]
  );

  const requireBidderOtpRef = useRef<() => void>(() => {});
  const registerBidderOtp = useCallback((requireBidderOtp: () => void) => {
    requireBidderOtpRef.current = requireBidderOtp;
  }, []);
  const interceptBidderOtpError = useCallback(
    (report: PaymentErrorReport) => {
      if (isAuction && report.rawErrorCode === "bidder_otp_required") {
        requireBidderOtpRef.current();
        return true;
      }
      return false;
    },
    [isAuction]
  );

  const paymentErrorState = usePaymentErrorHandler({
    formId,
    commandId: commandId ?? null,
    organization: formOrganization,
    setIsSubmitting: formSetIsSubmitting,
    setIsPadLoading: formSetIsPadLoading,
    formAmplitudeContext,
    formType: feFormType,
    tip: formTip,
    selectedPaymentMethod: feSelectedPaymentMethod,
    isEmbed: feIsEmbed,
    stripeRecurrenceInterval: feStripeRecurrenceInterval,
    currency: feCurrency,
    category: feCategory,
    prioritizeBankPayment: fePrioritizeBankPayment,
    expressCheckoutDisabled: feExpressCheckoutDisabled,
    paymentMode: fePaymentMode,
    onPaymentError: onReopenCommandAfterFormPaymentError,
    interceptError: interceptBidderOtpError,
  });

  const logoHref = useThemedFormZeffyLogoUrl(utm);

  const isFreeTicket = displayedFormAmount === 0 && formType === FormType.Ticketing;

  const buyerInfoRenderPosition = useBuyerInformationRenderPosition();

  const weeklySupportersCount = formObject.weeklySupportersCount;
  const organizationName = formObject.organization?.name;
  const shouldDisplayPeerCredibilityMessage = weeklySupportersCount && weeklySupportersCount > 5;

  if (formType === FormType.DonationForm && frontendFormContext.dafFlow) {
    return <DafPaymentFlow {...frontendFormContext.dafFlow} />;
  }

  return (
    <PaymentErrorContext.Provider value={paymentErrorState}>
      {isAuction && <BidderOtpController onReady={registerBidderOtp} />}
      <FormV2Step2Layout
        buyerInformationPosition={buyerInfoRenderPosition}
        isEmbed={isEmbed}
        slots={{
          backButton: (
            <FormButton
              variant="outline"
              onClick={() => setCurrentStep(hasAddOnsStep ? "addOns" : "selection")}
              startIcon={<ChevronLeft />}
            >
              {t("ticketing", "back")}
            </FormButton>
          ),
          peerCredibilityMessage: shouldDisplayPeerCredibilityMessage && (
            <Typography variant="subtitle2" marginBottom={2} sx={{ color: theme.palette.forms.text.brand }}>
              {t("ticketing", "peerCredibilityMessage", {
                count: weeklySupportersCount,
                organizationName,
              })}
            </Typography>
          ),
          buyerInformation: <BuyerInformationSection />,
          shippingSection: <ShippingAddressSection />,
          buyerQuestions: <BuyerQuestionsSection />,
          fulfillmentSection: formType === FormType.Ticketing ? <FulfillmentSection /> : null,
          summarySection: <SummarySection />,
          paymentSection: (
            <PaymentSection
              paymentErrorState={paymentErrorState}
              isLiveFormEditorPreview={isLiveFormEditorPreview}
              isPaymentSectionGhostPreview={isPaymentSectionGhostPreview}
              eligibilitySlot={formType === FormType.Ticketing ? <RaffleEligibility /> : null}
            />
          ),
          submitButton: (
            <>
              {!isFreeTicket && !hasAlreadyBid && <SubmitCommandButton />}
              {isAuction && hasAlreadyBid && <SubmitNewBidsButton />}
              {isFreeTicket && <SubmitFreeTicketingButton />}
            </>
          ),
          userAgreement: <FormUserAgreement />,
          zeffyLogo: <ThemedZeffyLogo href={logoHref} onClick={onTrackLogoClick} />,
        }}
      />
    </PaymentErrorContext.Provider>
  );
};
